import { reverse } from 'named-urls'
import React from 'react'
import { Link, useLocation } from 'react-router-dom'

import type { BannerProps } from '@fourthwall/components'
import type { LocationState } from '@types'
import { routing } from '@utils/routing'

import { useGiveawayContributionQuery } from '../../Giveaway'
import { useGiftQuery } from '../queries/useGiftQuery'
import { useOrderGroupedByShipmentsQuery } from '../queries/useOrderGroupedByShipmentsQuery'
import { useOrderIdParam } from './useOrderIdParam'

export const useOrderBanners = () => {
  const { orderId } = useOrderIdParam()
  const { orderGroupedByShipmentsQuery } = useOrderGroupedByShipmentsQuery([{ orderId }])
  const { giftQuery } = useGiftQuery([{ giftId: orderGroupedByShipmentsQuery.data?.giftId! }], {
    enabled: !!orderGroupedByShipmentsQuery.data?.giftId,
  })
  const { giveawayContributionQuery } = useGiveawayContributionQuery(
    [{ giveawayId: giftQuery.data?.giveawayId! }],
    { enabled: !!giftQuery.data?.giveawayId },
  )

  const location = useLocation()
  const locationState: LocationState = {
    from: { label: `Order ${orderGroupedByShipmentsQuery.data?.friendlyId}`, location },
  }

  const getGiveawayBanner = (): BannerProps | undefined => {
    if (!giftQuery.data) return

    if (!giftQuery.data.giveawayId) {
      return { children: 'This is a giveaway order.', appearance: 'warning' }
    }

    if (!giveawayContributionQuery.data) return

    const { supporter, giveawayId, friendlyId } = giveawayContributionQuery.data
    const by = supporter.username || supporter.email

    return {
      children: (
        <>
          This order is a redeemed gift funded by {by} (
          <Link
            to={reverse(routing.contributions.orders.giveaways.giveaway, { giveawayId })}
            state={locationState}
          >
            #{friendlyId}
          </Link>
          )
        </>
      ),
      appearance: 'warning',
    }
  }

  const getGiftAsPromotionRedeemBanner = (): BannerProps | undefined => {
    if (orderGroupedByShipmentsQuery.data?.promotion?.creationReason?.type !== 'FROM_GIFT') return

    // eslint-disable-next-line no-unsafe-optional-chaining
    const { giveawayId } = orderGroupedByShipmentsQuery.data?.promotion?.creationReason

    if (!giveawayId) return

    return {
      children: (
        <>
          This order used a promo code that came from a{' '}
          <Link
            to={reverse(routing.contributions.orders.giveaways.giveaway, { giveawayId })}
            state={locationState}
          >
            purchased gift
          </Link>
          .{' '}
          <a
            href={routing.external.helpCenter.twitchGifting}
            target="_blank"
            rel="noopener noreferrer"
          >
            Learn more
          </a>
        </>
      ),
      appearance: 'warning',
    }
  }

  const getSalesChannelBanner = (): BannerProps | undefined => {
    const type = orderGroupedByShipmentsQuery.data?.salesChannel?.type

    if (!type) return

    if (type === 'TikTok') {
      return {
        children: (
          <>
            This order was placed through TikTok Shop.{' '}
            <a
              href={routing.external.helpCenter.tiktokShop}
              target="_blank"
              rel="noopener noreferrer"
            >
              Learn more
            </a>
            <br />
            <a
              href={`${routing.external.tiktok.orderDetails}?order_no=${orderGroupedByShipmentsQuery.data?.salesChannel?.id}&shop_region=US`}
              target="_blank"
              rel="noopener noreferrer"
            >
              View order details in TikTok Seller Center
            </a>
          </>
        ),
        appearance: 'warning',
      }
    }

    if (type === 'Meta') {
      return {
        children: (
          <>
            This order was placed through Meta Shop.{' '}
            <a href={routing.external.helpCenter.meta} target="_blank" rel="noreferrer">
              Learn more
            </a>
            <br />
            <a
              href={`https://business.facebook.com/commerce/${orderGroupedByShipmentsQuery.data?.salesChannel?.cmsId}/orders/${orderGroupedByShipmentsQuery.data?.salesChannel?.id}`}
              target="_blank"
              rel="noreferrer"
            >
              View order details in Meta Commerce Manager
            </a>
          </>
        ),
        appearance: 'warning',
      }
    }
  }

  const banners: (BannerProps | undefined)[] = [
    getGiveawayBanner(),
    getGiftAsPromotionRedeemBanner(),
    getSalesChannelBanner(),
  ].filter(Boolean) as BannerProps[]

  return {
    banners,
  }
}
